import * as React from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../config/firebase";
import {
  useCreateProject,
  useDeleteProject,
  useListUserProjects,
  useUpdateProject,
  useListBrandTemplates,
} from "@dataconnect/generated/react";
import {
  ProjectSpace,
  ProjectSpaceData,
  BrandIdentity,
  StudioProduct,
  GeneratedContent,
} from "../types.ts";
import ProjectDashboard from "../components/ProjectDashboard.tsx";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { deserializeBrandTemplate } from "../utils/brandTemplateHelpers";
import { sortProjectsByLastActive } from "../utils/projectSorting";
import {
  trackProjectCreated,
  trackProjectDeleted,
  trackQuickstartSkipped,
  trackQuickstartStageReached,
} from "../services/analyticsService";
import { useQuickStartTour } from "../hooks/useQuickStartTour";

const App: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/" });

  // Auth State - get from auth.currentUser since __root.tsx manages auth state
  const user = auth.currentUser;
  const [error, setError] = React.useState<string | null>(null);
  const { activeFlow: quickStartFlow, skip: skipQuickStart } =
    useQuickStartTour();
  const openProjectStageEmittedRef = React.useRef(false);

  // Data Connect Hooks
  const {
    data: projectsData,
    refetch: refetchProjects,
    isLoading: isLoadingProjects,
    isError: isProjectsError,
  } = useListUserProjects({
    enabled: !!user,
  });
  const { mutateAsync: createProject } = useCreateProject();
  const { mutateAsync: updateProject } = useUpdateProject();
  const { mutateAsync: deleteProject } = useDeleteProject();
  const {
    data: brandsData,
    refetch: refetchBrands,
    isLoading: isLoadingBrands,
  } = useListBrandTemplates({ enabled: !!user });

  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Project Management State
  const projects: ProjectSpace[] = React.useMemo(() => {
    if (!projectsData?.user?.projects) return [];
    return sortProjectsByLastActive(
      projectsData.user.projects.map((p) => {
        let data: ProjectSpaceData;
        try {
          // Map studio products with their latest generated content
          // For the dashboard, we only need the latest image from each product for the preview
          // Type assertion: ListUserProjects query returns studioProducts; generated types may omit it
          const projectProducts =
            (
              p as {
                studioProducts?: Array<{
                  id: string;
                  generatedContentLatest?: Array<{
                    id: string;
                    imageUrl?: string | null;
                    createdAt: string;
                  }>;
                }>;
              }
            ).studioProducts ?? [];
          const products: StudioProduct[] = projectProducts.map((sp) => {
            const latestGenerated = sp.generatedContentLatest?.[0];
            let imageUrl = latestGenerated?.imageUrl || null;

            // Note: imageUrl can be either a storage path (f/...) or a data URL (data:...)
            // For dashboard preview, we'll use it as-is and load it async if needed
            const generatedContent: GeneratedContent[] = latestGenerated
              ? [
                  {
                    id: latestGenerated.id,
                    image: imageUrl,
                    text: null,
                    video: null,
                    timestamp: new Date(latestGenerated.createdAt).getTime(),
                  },
                ]
              : [];

            return {
              id: sp.id,
              image: { id: "", base64: "", mimeType: "", name: "" },
              originalImage: { id: "", base64: "", mimeType: "", name: "" },
              context: "",
              info: "",
              itemCount: null,
              isSelected: false,
              isProcessing: false,
              generatedContent,
              isGenerating: false,
              error: null,
            };
          });

          data = {
            products,
            multiProductScenes: [],
            retailerProfiles: [],
            highlightsConfigs: [],
            workflows: [],
            settings: {
              defaultBackgroundColor: "#FFFFFF",
              logoPosition: "bottom-right",
              dimensionMarker: "both",
              companyLogo: null,
              useAvatar: false,
            },
          };
        } catch (e) {
          console.error("Failed to parse project data for", p.id, e);
          data = {
            products: [],
            multiProductScenes: [],
            retailerProfiles: [],
            highlightsConfigs: [],
            workflows: [],
            settings: {
              defaultBackgroundColor: "#FFFFFF",
              logoPosition: "bottom-right",
              dimensionMarker: "both",
              companyLogo: null,
              useAvatar: false,
            },
          };
        }
        return {
          id: p.id,
          name: p.name,
          lastModified: new Date(p.updatedAt).getTime(),
          data: data,
          brandTemplate: p.brandTemplate
            ? { id: p.brandTemplate.id, name: p.brandTemplate.name }
            : undefined,
        };
      })
    );
  }, [projectsData]);

  // Global Brand Library State
  const globalBrands: BrandIdentity[] = React.useMemo(() => {
    if (!brandsData?.brandTemplates) return [];
    return brandsData.brandTemplates.map((template) =>
      deserializeBrandTemplate(template as any)
    );
  }, [brandsData]);

  // Fire quickstart_stage_reached once per page mount when the dashboard
  // shows the highlighted project card. The ref de-bounces so navigation
  // away and back doesn't re-emit within the same session.
  React.useEffect(() => {
    if (
      quickStartFlow !== null &&
      projects.length > 0 &&
      !openProjectStageEmittedRef.current
    ) {
      openProjectStageEmittedRef.current = true;
      trackQuickstartStageReached("open_project", quickStartFlow);
    }
  }, [quickStartFlow, projects.length]);

  const handleSelectProject = (id: string) => {
    const params = { projectId: id };
    // The highlighted quick-start project resumes on the tab whose tour is
    // running; the Studio only renders the images tour.
    const isTourProject = quickStartFlow !== null && projects[0]?.id === id;
    if (isTourProject && quickStartFlow === "highlights") {
      navigate({ to: "/projects/$projectId/highlights", params });
    } else if (isTourProject && quickStartFlow === "video") {
      navigate({ to: "/projects/$projectId/video", params });
    } else {
      navigate({ to: "/projects/$projectId", params });
    }
  };

  const handleSkipQuickStart = () => {
    trackQuickstartSkipped("open_project", quickStartFlow ?? "images");
    skipQuickStart();
  };

  const handleCreateProject = async (name: string, brandId: string) => {
    if (!brandId) {
      setError(t("dashboard.errorSelectBrand"));
      return;
    }
    try {
      const result = await createProject({
        name,
        brandTemplateId: brandId,
      });

      await refetchProjects();

      // The result contains the newly created project's ID
      const newId = result.project_insert.id;
      trackProjectCreated(newId);
      handleSelectProject(newId);
    } catch (err) {
      console.error("Failed to create project:", err);
      setError(t("dashboard.errorCreateProject"));
    }
  };

  const handleRenameProject = async (id: string, newName: string) => {
    const project = projects.find((p) => p.id === id);
    if (!project) return;
    try {
      await updateProject({
        id,
        name: newName,
      });
      refetchProjects();
    } catch (err) {
      console.error("Failed to rename project:", err);
      setError(t("dashboard.errorRenameProject"));
    }
  };

  const handleDuplicateProject = async (id: string, newName: string) => {
    const source = projects.find((p) => p.id === id);
    if (!source) return;
    if (!source.brandTemplate?.id) {
      setError(t("dashboard.errorSelectBrand"));
      return;
    }

    try {
      await createProject({
        name: newName,
        brandTemplateId: source.brandTemplate.id,
      });
      refetchProjects();
    } catch (err) {
      console.error("Failed to duplicate project:", err);
      setError(t("dashboard.errorDuplicateProject"));
    }
  };

  const handleDeleteProject = async (id: string) => {
    if (window.confirm(t("dashboard.confirmDeleteProject"))) {
      try {
        await deleteProject({ id });
        trackProjectDeleted(id);
        await refetchProjects();
      } catch (err) {
        console.error("Failed to delete project:", err);
        setError(t("dashboard.errorDeleteProject"));
      }
    }
  };

  if (!user) {
    return null; // Redirect handled by __root.tsx
  }

  return (
    <>
      <ProjectDashboard
        projects={projects}
        isLoadingProjects={isLoadingProjects}
        projectsLoadFailed={isProjectsError && projectsData === undefined}
        onRetryProjects={() => void refetchProjects()}
        onCreateProject={handleCreateProject}
        onRenameProject={handleRenameProject}
        onDuplicateProject={handleDuplicateProject}
        onSelectProject={handleSelectProject}
        onDeleteProject={handleDeleteProject}
        globalBrands={globalBrands}
        loadingBrands={isLoadingBrands}
        onAddBrand={() =>
          navigate({ to: "/onboarding", search: { returnTab: "library" } })
        }
        refetchBrands={refetchBrands}
        initialTab={search.returnTab || "projects"}
        initialCreateBrandId={search.createWithBrandId}
        quickStartProjectId={
          quickStartFlow !== null ? (projects[0]?.id ?? null) : null
        }
        onSkipQuickStart={handleSkipQuickStart}
      />
    </>
  );
};

interface IndexSearch {
  returnTab?: "projects" | "library";
  createWithBrandId?: string;
}

export const Route = createFileRoute("/")({
  component: App,
  validateSearch: (search: Record<string, unknown>): IndexSearch => {
    return {
      returnTab:
        (search.returnTab as "projects" | "library" | undefined) || undefined,
      createWithBrandId:
        typeof search.createWithBrandId === "string"
          ? search.createWithBrandId
          : undefined,
    };
  },
});
